/**
 * JWT Token Utilities
 *
 * Utilities for decoding and validating JWT tokens on the client side.
 * Note: These are for client-side validation only and should not be used
 * for security-critical operations. Always verify tokens on the server.
 */

/**
 * Decode a JWT token without verification
 * @param token - JWT token string
 * @returns Decoded token payload or null if invalid
 */
export function decodeToken(token: string): any | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
}

/**
 * Check if a JWT token is expired
 * @param token - JWT token string
 * @returns true if token is expired, false otherwise
 */
export function isTokenExpired(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const now = Date.now() / 1000;
  return decoded.exp < now;
}

/**
 * Get the expiration time of a JWT token
 * @param token - JWT token string
 * @returns Date object representing expiration time, or null if invalid
 */
export function getTokenExpiration(token: string): Date | null {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return null;

  return new Date(decoded.exp * 1000);
}

/**
 * Check if a token needs to be refreshed
 * Returns true if token expires within 5 minutes
 * @param token - JWT token string
 * @returns true if token should be refreshed, false otherwise
 */
export function needsRefresh(token: string): boolean {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) return true;

  const now = Date.now() / 1000;
  const expiryBuffer = 5 * 60; // 5 minutes in seconds
  return decoded.exp - now < expiryBuffer;
}

/**
 * Legacy tokenUtils object for backward compatibility
 * @deprecated Use named exports instead
 */
export const tokenUtils = {
  decodeToken,
  isTokenExpired,
  getTokenExpiration,
  needsRefresh,
};
